import { ReactElement, useCallback, useMemo } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { publishClipPlaneCreateEvent } from '@ui/events'
import { useModelStore } from '@editorStores'
import { DetectedOpeningPlane, OPENING_ALIGN_BRACKET_ID, detectOpeningPlanes } from './utils'

interface OpeningPlaneListProps {
  activePlaneId: string | null
  onSelectPlane: (plane: DetectedOpeningPlane) => void
}

/**
 * Lists the planes whose openings need an alignment check during the "Öffnungen ausrichten" step
 * (walls whose faces share a plane are collapsed into one entry; planes whose openings all sit in
 * one wall are omitted). Selecting a plane drops a clip bracket ("Schnittklammer") on it so the
 * user can verify the openings line up, and marks that plane as active.
 */
const OpeningPlaneList = ({
  activePlaneId,
  onSelectPlane,
}: OpeningPlaneListProps): ReactElement => {
  const walls = useModelStore(state => state.model.walls)

  const planes = useMemo(() => detectOpeningPlanes(walls), [walls])

  const selectPlane = useCallback(
    (plane: DetectedOpeningPlane) => {
      publishClipPlaneCreateEvent({
        normal: { x: plane.normal.x, y: plane.normal.y, z: plane.normal.z },
        point: { x: plane.point.x, y: plane.point.y, z: plane.point.z },
        mode: 'bracket',
        replaceExisting: true,
        id: OPENING_ALIGN_BRACKET_ID,
      })
      onSelectPlane(plane)
    },
    [onSelectPlane],
  )

  if (planes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine Ebenen gefunden, in denen Öffnungen mehrerer Wände ausgerichtet werden müssen.
      </Typography>
    )
  }

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Wählen Sie eine Ebene aus, um dort eine Schnittklammer anzulegen und die Öffnungen zu
        prüfen.
      </Typography>

      {planes.map((plane, index) => {
        const openingLabel = plane.openingCount === 1 ? 'Öffnung' : 'Öffnungen'
        const wallLabel = plane.wallGuids.length === 1 ? 'Wand' : 'Wände'
        const isActive = plane.id === activePlaneId

        return (
          <Button
            key={plane.id}
            variant={isActive ? 'contained' : 'outlined'}
            size="small"
            fullWidth
            disableElevation
            onClick={() => selectPlane(plane)}
            data-cy={`btn-opening-plane-${index}`}
            aria-pressed={isActive}
            sx={{ justifyContent: 'space-between', textTransform: 'none' }}
          >
            <span>{`Ebene ${index + 1}`}</span>
            <Typography
              variant="caption"
              color={isActive ? 'inherit' : 'text.secondary'}
              sx={{ opacity: isActive ? 0.85 : 1 }}
            >
              {`${plane.openingCount} ${openingLabel} · ${plane.wallGuids.length} ${wallLabel}`}
            </Typography>
          </Button>
        )
      })}
    </Stack>
  )
}

export default OpeningPlaneList
